const FREQUENCIES: Record<string, number> = {
    G4: 392.0,
    A4: 440.0,
    B4: 493.88,
    C5: 523.25,
    D5: 587.33,
    E5: 659.25,
    F5: 698.46,
    G5: 783.99
}

/* "Happy Birthday to You", note and length in beats. The melody is public domain. */
const TUNE: Array<[string, number]> = [
    ['G4', 0.75], ['G4', 0.25], ['A4', 1], ['G4', 1], ['C5', 1], ['B4', 2],
    ['G4', 0.75], ['G4', 0.25], ['A4', 1], ['G4', 1], ['D5', 1], ['C5', 2],
    ['G4', 0.75], ['G4', 0.25], ['G5', 1], ['E5', 1], ['C5', 1], ['B4', 1], ['A4', 2],
    ['F5', 0.75], ['F5', 0.25], ['E5', 1], ['C5', 1], ['D5', 1], ['C5', 3]
]

type AudioContextConstructor = typeof AudioContext

export const createAudioContext = (): AudioContext | null => {
    if (typeof window === 'undefined') return null

    const Constructor: AudioContextConstructor | undefined = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext

    if (!Constructor) return null

    try {
        return new Constructor()
    }
    catch {
        return null
    }
}

/**
 * Plays the birthday tune with a soft music box sound.
 * Returns a function that fades it out early.
 */
export function playBirthdayTune(existing?: AudioContext | null): () => void {
    const context = existing ?? createAudioContext()

    if (!context) return () => undefined

    if (context.state === 'suspended') void context.resume()

    const master = context.createGain()
    const beat = 0.42
    let time = context.currentTime + 0.12

    master.gain.value = 0.16
    master.connect(context.destination)

    for (const [note, beats] of TUNE) {
        const frequency = FREQUENCIES[note]
        const length = beats * beat
        const body = context.createOscillator()
        const shimmer = context.createOscillator()
        const shimmerLevel = context.createGain()
        const envelope = context.createGain()

        body.type = 'triangle'
        body.frequency.value = frequency
        shimmer.type = 'sine'
        shimmer.frequency.value = frequency * 2
        shimmerLevel.gain.value = 0.22

        body.connect(envelope)
        shimmer.connect(shimmerLevel)
        shimmerLevel.connect(envelope)
        envelope.connect(master)

        envelope.gain.setValueAtTime(0.0001, time)
        envelope.gain.exponentialRampToValueAtTime(1, time + 0.015)
        envelope.gain.exponentialRampToValueAtTime(0.0001, time + length + 0.45)

        body.start(time)
        shimmer.start(time)
        body.stop(time + length + 0.5)
        shimmer.stop(time + length + 0.5)

        time += length
    }

    return () => {
        master.gain.setTargetAtTime(0, context.currentTime, 0.08)
    }
}
